export interface ProcessedVideoData {
  thumbnail: string;
  frames: string[];
  duration: number;
}

export const processVideoFile = (file: File, frameCount = 20): Promise<ProcessedVideoData> =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const canvas = document.createElement('canvas');
    const context = canvas.getContext('2d');

    if (!context) {
      reject(new Error('Could not create canvas context'));
      return;
    }

    const TARGET_WIDTH = 640;

    video.autoplay = false;
    video.muted = true;
    video.src = URL.createObjectURL(file);

    video.onloadedmetadata = async () => {
      const duration = video.duration;
      const frames: string[] = [];

      const aspectRatio = video.videoWidth / video.videoHeight;
      canvas.width = TARGET_WIDTH;
      canvas.height = TARGET_WIDTH / aspectRatio;

      const thumbnailTime = Math.min(1, duration * 0.1);
      video.currentTime = thumbnailTime;

      const captureFrame = () => {
        context.drawImage(video, 0, 0, canvas.width, canvas.height);
        return canvas.toDataURL('image/jpeg', 0.7);
      };

      const seek = (time: number): Promise<void> =>
        new Promise((res) => {
          const onSeeked = () => {
            video.removeEventListener('seeked', onSeeked);
            res();
          };
          video.addEventListener('seeked', onSeeked);
          video.currentTime = time;
        });

      try {
        await seek(thumbnailTime);
        const thumbnail = captureFrame();

        const interval = duration / (frameCount + 1);

        for (let i = 1; i <= frameCount; i++) {
          await seek(interval * i);
          frames.push(captureFrame());
        }

        URL.revokeObjectURL(video.src);
        resolve({
          thumbnail,
          frames,
          duration
        });
      } catch (err) {
        URL.revokeObjectURL(video.src);
        reject(err);
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(video.src);
      reject(new Error('Failed to load video'));
    };
  });
